import { apiURL } from './constants';
import axios from 'axios';

const baseURL = `${apiURL}/api/tracking_event`;

function getAuthHeaders() {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
}

/**
 * Shipment tracking page payload (timeline + ETAs + piece status summary).
 * GET /api/tracking_event/shipment/<shipment_id>/
 * Permission: tracking_events_list
 *
 * Same response shape as GET /api/customer/shipment/<id>/tracking-events/.
 */
export async function getShipmentTrackingPage(shipmentId) {
  getAuthHeaders();
  const response = await axios.get(`${baseURL}/shipment/${shipmentId}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Get shipment tracking page error:', error);
    throw error;
  });
  return response;
}

/**
 * List tracking events with filters and search.
 * Permission: tracking_events_list
 * @param {Object} params - Query params: page, page_size, pagination, search, id, shipment, event_type,
 *   status_snapshot, location, created_by, deleted, timestamp, timestamp_start, timestamp_end,
 *   created_at_start, created_at_end, modified_at_start, modified_at_end
 */
export async function listTrackingEvents(params = {}) {
  getAuthHeaders();
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (key === 'location' && typeof value === 'object' && value && value.id != null) {
        searchParams.append(key, value.id);
      } else if (typeof value === 'object' && value && value.id != null) {
        searchParams.append(key, value.id);
      } else {
        searchParams.append(key, value);
      }
    }
  });
  const response = await axios.get(`${baseURL}/?${searchParams.toString()}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('List tracking events error:', error);
    throw error;
  });
  return response;
}

/**
 * Get a single tracking event by ID.
 * Permission: tracking_events_view
 */
export async function getTrackingEvent(id) {
  getAuthHeaders();
  const response = await axios.get(`${baseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Get tracking event error:', error);
    throw error;
  });
  return response;
}

/**
 * Create a tracking event.
 * Permission: tracking_events_create
 * Body: shipment (required), event_type (required), status_snapshot, location, timestamp, description,
 *   update_shipment_status, new_status, current_location_text, add_current_location_record, send_notification
 */
export async function createTrackingEvent(body) {
  getAuthHeaders();
  const payload = { ...body };
  if (payload.location && typeof payload.location === 'object' && payload.location.id != null) {
    payload.location = payload.location.id;
  }
  const response = await axios.post(`${baseURL}/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Create tracking event error:', error);
    throw error;
  });
  return response;
}

/**
 * Update (full or partial) a tracking event.
 * Permission: tracking_events_update
 *
 * Writable fields include `timestamp` (ISO 8601 UTC). Include `timestamp` only when changing it:
 * the server re-syncs parent shipment state from the latest event by timestamp when this key
 * is present; otherwise it syncs from the edited event directly.
 */
export async function updateTrackingEvent(id, body, partial = true) {
  getAuthHeaders();
  const payload = { ...body };
  if (payload.location && typeof payload.location === 'object' && payload.location.id != null) {
    payload.location = payload.location.id;
  }
  if (payload.shipment && typeof payload.shipment === 'object' && payload.shipment.id != null) {
    payload.shipment = payload.shipment.id;
  }
  if (payload.created_by && typeof payload.created_by === 'object' && payload.created_by.id != null) {
    payload.created_by = payload.created_by.id;
  }
  const method = partial ? 'patch' : 'put';
  const response = await axios[method](`${baseURL}/${id}/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Update tracking event error:', error);
    throw error;
  });
  return response;
}

/**
 * Soft-delete a tracking event.
 * Permission: tracking_events_delete
 */
export async function deleteTrackingEvent(id) {
  getAuthHeaders();
  const response = await axios.delete(`${baseURL}/${id}/`, {
    withCredentials: true,
  }).catch(error => {
    console.error('Delete tracking event error:', error);
    throw error;
  });
  return response;
}

/**
 * Bulk update tracking events.
 * Permission: tracking_events_bulk_update
 * Body: array of { id, ...fields }. Each item is a partial patch.
 *
 * Writable per item: `timestamp`, `event_type`, `status_snapshot`, `location`, `description`,
 * `shipment`, `created_by`. When `timestamp` is included, parent shipment state is reconciled
 * from the latest remaining event by timestamp for that shipment.
 */
export async function bulkUpdateTrackingEvents(items) {
  getAuthHeaders();
  const payload = items.map(item => {
    const { id, ...rest } = item;
    const out = { id, ...rest };
    if (out.location && typeof out.location === 'object' && out.location.id != null) out.location = out.location.id;
    if (out.shipment && typeof out.shipment === 'object' && out.shipment.id != null) out.shipment = out.shipment.id;
    if (out.created_by && typeof out.created_by === 'object' && out.created_by.id != null) out.created_by = out.created_by.id;
    return out;
  });
  const response = await axios.put(`${baseURL}/bulk-update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Bulk update tracking events error:', error);
    throw error;
  });
  return response;
}

/**
 * Bulk delete (soft) tracking events.
 * Permission: tracking_events_bulk_delete
 * Body: array of IDs
 */
export async function bulkDeleteTrackingEvents(ids) {
  getAuthHeaders();
  const response = await axios.delete(`${baseURL}/bulk-delete/`, {
    data: ids,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch(error => {
    console.error('Bulk delete tracking events error:', error);
    throw error;
  });
  return response;
}

/**
 * Static fallback catalog when GET /api/tracking_event_type/ is unavailable.
 * Prefer useTrackingEventTypeOptions() — it localizes labels for the active UI language.
 */
export const TRACKING_EVENT_TYPES = [
  { value: 'order_confirmed', label: 'Order confirmed' },
  { value: 'awaiting_pickup', label: 'Awaiting pickup' },
  { value: 'picked_up_origin', label: 'Picked up – origin' },
  { value: 'at_warehouse_origin', label: 'At warehouse – origin' },
  { value: 'departed_origin_port', label: 'Departed origin – port' },
  { value: 'departed_origin_airport', label: 'Departed origin – airport' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'arrived_destination_port', label: 'Arrived destination – port' },
  { value: 'arrived_destination_airport', label: 'Arrived destination – airport' },
  { value: 'departed_destination_airport', label: 'Departed destination – airport' },
  { value: 'customs_cleared_destination', label: 'Customs cleared – destination' },
  { value: 'at_dc_destination', label: 'At distribution center – destination' },
  { value: 'out_for_delivery', label: 'Out for delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'other', label: 'Other' },
];
